import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/estimatorxpress-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | EstimatorXpress Alternative",
  "description": "Looking for an EstimatorXpress cloud alternative? The Proper Tool offers AI-powered quoting, a UK cost library, and no desktop install. From £29/mo with a 14-day free trial.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "EstimatorXpress Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best EstimatorXpress alternative?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK builders who want cloud-based quoting without the desktop workflow and manual input of EstimatorXpress, The Proper Tool is one alternative. EstimatorXpress annual subscriptions are £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT, about £75 to £108 per month equivalent; perpetual options are also available.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there a cloud version of EstimatorXpress?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, EstimatorXpress has a cloud version. However, it retains the workflow of the original desktop application, requiring significant manual input for each estimate. It does not offer AI scope generation or automatic drawing interpretation. Builders looking for a truly cloud-native tool with modern AI-assisted quoting typically find the EstimatorXpress cloud version still feels like the desktop product accessed through a browser.",
      },
    },
    {
      "@type": "Question",
      "name": "How does EstimatorXpress pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EstimatorXpress lists annual subscriptions at £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT, roughly £75 to £108 per month. Perpetual licences are £1,599, £2,199 and £2,799 plus £29.99/month support and updates. A free trial is offered, but its duration is not published.",
      },
    },
    {
      "@type": "Question",
      "name": "Can EstimatorXpress read structural drawings automatically?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. EstimatorXpress does not automatically read architectural drawings or PDFs. All items must be entered or selected manually from its pricing library. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, spans, and dimensions to generate a more complete and accurate quote.",
      },
    },
    {
      "@type": "Question",
      "name": "Is EstimatorXpress good for loft conversions and extensions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EstimatorXpress is primarily designed for house builders and developers doing volume new-build work. Its pricing libraries and workflows are optimised for traditional new-build estimating rather than the alteration, extension, and conversion work that smaller residential builders predominantly do. The Proper Tool is purpose-built for extensions, loft conversions, and structural alterations, including reading the drawings that architects produce for these projects.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  ex: string;
  exState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "UK construction pricing", ex: "Yes: UK-specific library", exState: "yes", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "AI scope generation", ex: "No: manual entry only", exState: "no", tpt: "Yes: from description or drawings", tptState: "yes" },
  { feature: "Reads drawings and PDFs automatically", ex: "Pro+Take-Off: partial/manual", exState: "partial", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Cloud-native workflow", ex: "Partial: ported from desktop", exState: "partial", tpt: "Yes: built for the browser", tptState: "yes" },
  { feature: "Mobile-friendly", ex: "Partial", exState: "partial", tpt: "Yes", tptState: "yes" },
  { feature: "Suited to extensions and alterations", ex: "Limited: new-build focus", exState: "partial", tpt: "Yes: built for this work", tptState: "yes" },
  { feature: "Scope completeness validation", ex: "No", exState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", ex: "Yes (detailed)", exState: "yes", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Volume new-build estimating", ex: "Strong", exState: "yes", tpt: "Residential alterations focus", tptState: "partial" },
  { feature: "Free trial available", ex: "Yes: duration not published", exState: "partial", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", ex: "£899/year Essential + VAT", exState: "partial", tpt: "£29/mo (per account)", tptState: "yes" },
  { feature: "Time to first quote", ex: "Days of setup and training", exState: "no", tpt: "Under 10 minutes", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Desktop-era workflow in the cloud",
    desc: "EstimatorXpress began as a Windows desktop application. Its cloud version makes the product browser-accessible, but the underlying workflow (manual selection of items, manual quantity entry) remains. Builders looking for a genuinely modern, cloud-native experience find it still feels like the old desktop product.",
  },
  {
    title: "No evidence of AI scope-to-price generation",
    desc: "EstimatorXpress provides structured estimating and Pro+Take-Off supports plan takeoff, but there is no evidence of AI reading a job description or drawing and generating a priced scope automatically.",
  },
  {
    title: "Primarily a house-builder tool",
    desc: "EstimatorXpress was built for and is most effective for volume house building: new-build residential projects with standard house types. For builders primarily doing extensions, loft conversions, structural alterations, and one-off residential work, many of its estimating templates are less relevant.",
  },
  {
    title: "Steep learning curve and training requirement",
    desc: "HBXL, the company behind EstimatorXpress, typically recommends formal training before productive use. For small builders and sole traders who want to start quoting quickly, the learning investment before the tool delivers value is a real obstacle.",
  },
  {
    title: "Higher price for smaller builders",
    desc: "Annual subscriptions are £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT, about £75 to £108 per month equivalent. Perpetual options are £1,599, £2,199 and £2,799 plus £29.99/month support and updates.",
  },
];

const STAY_ON_ESTIMATORXPRESS = [
  "You are a house builder or housing association doing volume new-build residential work where EstimatorXpress's traditional estimating depth is directly relevant.",
  "You have already invested significant time in learning the product and have templates and workflows in place.",
  "Your team does traditional detailed material takeoffs and you value the depth of the pricing library over quoting speed.",
  "You are a developer or housing association that needs the detailed labour and materials breakdown format that EstimatorXpress produces.",
];

const SWITCH_REASONS = [
  "You want to generate quotes from drawings or a project description without lengthy manual input.",
  "Your work is extensions, loft conversions, and structural alterations rather than ground-up house building.",
  "You want a genuinely cloud-native tool that works on any device without feeling like a legacy desktop application.",
  "You need to be quoting immediately, not after a training course or days of setup.",
  "You want a client-friendly PDF output that presents your quote professionally, not a traditional estimating-format document.",
  "You want a 14-day free trial with no credit card, so you can verify the tool works for your jobs before committing.",
];

const FAQS = [
  {
    q: "What is the best EstimatorXpress alternative?",
    a: "For UK builders who want cloud-based quoting without the desktop workflow and manual input of EstimatorXpress, The Proper Tool is the strongest alternative. It generates fully itemised scopes from drawings or a plain description using AI, has a 31-category UK cost library built in, and starts at £29/month with a 14-day free trial.",
  },
  {
    q: "Is there a cloud version of EstimatorXpress?",
    a: "Yes, EstimatorXpress has a cloud version. However, it retains the workflow of the original desktop application. Significant manual input is required for each estimate. It does not offer AI scope generation or automatic drawing interpretation.",
  },
  {
    q: "How does EstimatorXpress pricing compare to The Proper Tool?",
    a: "EstimatorXpress annual subscriptions are £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT, about £75 to £108 per month equivalent. Perpetual options are £1,599, £2,199 and £2,799 plus £29.99/month support and updates.",
  },
  {
    q: "Can EstimatorXpress read structural drawings automatically?",
    a: "No. EstimatorXpress does not automatically read architectural drawings or PDFs. All items must be entered or selected manually from its pricing library. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete quote.",
  },
  {
    q: "Is EstimatorXpress good for loft conversions and extensions?",
    a: "EstimatorXpress is primarily designed for house builders doing volume new-build work. For extension, loft conversion, and structural alteration work, The Proper Tool is purpose-built, including reading the structural drawings architects produce for these projects.",
  },
];

export default function EstimatorXpressAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="EstimatorXpress Alternative: Cloud-Based Quoting Without the Desktop Install | The Proper Tool"
        description="Looking for an EstimatorXpress alternative? The Proper Tool offers Smart Quoting from drawings, a UK cost library, and no desktop install. 14-day free trial from £29/mo."
        canonical="/estimatorxpress-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>EstimatorXpress Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              EstimatorXpress Alternative: cloud-based quoting without the desktop install
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              EstimatorXpress is a well-established UK estimating tool, but its desktop-era workflow isn't suited to every builder. Here's an honest comparison with The Proper Tool for builders who need faster, AI-assisted quoting.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Why builders look */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why builders look for an EstimatorXpress alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EstimatorXpress has genuine strengths, particularly its UK pricing library and depth of material takeoff for house builders. But builders consistently flag the same friction points:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs EstimatorXpress: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">EstimatorXpress</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.ex} state={row.exState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor pricing and feature information was checked on 19 September 2026. Prices and features may have changed. Verify directly with EstimatorXpress (HBXL) before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EstimatorXpress lists annual subscriptions at £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT. Perpetual licences are £1,599, £2,199 and £2,799 plus £29.99/month support and updates.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">EstimatorXpress</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />£899 to £1,299/year plus VAT (subscription)</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Free trial offered; duration not published</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Training often required before productive use</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Desktop-era workflow carries manual input overhead</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Most builders produce their first quote in under 10 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Who should stay */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on EstimatorXpress</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EstimatorXpress is a strong tool for a specific type of builder. If the following describes your business, it may still be the right choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_ESTIMATORXPRESS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why builders switch */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from EstimatorXpress to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically driven by quoting speed, work type, and the desire to work from drawings without manual entry.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with EstimatorXpress (HBXL) before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Related */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all 6 quoting tools for UK builders" },
                { href: "/buildxact-alternative", label: "Buildxact Alternative UK" },
                { href: "/simpro-alternative", label: "simPRO Alternative for Small Builders" },
                { href: "/tradify-alternative", label: "Tradify Alternative" },
                { href: "/comparisons", label: "All Software Comparisons" },
                { href: "/construction-estimating-software", label: "Construction Estimating Software" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
